/**
 * React Query hooks for Pulse's read endpoints.
 *
 * These wrap the existing `api.js` functions and add:
 *   - Automatic caching across page navigations (no spinner on /home → /inventory → /home)
 *   - Background refetch when the tab regains focus
 *   - Single retry on transient network failures
 *   - Shared query keys for easy invalidation (e.g. after a tenant switch)
 *
 * Adoption is gradual on purpose — pages keep their existing useEffect-based
 * fetching until they're consciously migrated. That way we never break a
 * working page just to "upgrade" it.
 */
import { useQuery } from "@tanstack/react-query";
import {
  getCherries,
  getInventoryKpis,
  getDeadCapitalBreakdown,
  getExecutiveBrief,
  getExecutiveActions,
  getDailyBrief,
  getSuggestedQuestions,
} from "./api";

/** Stable cache key namespace. Pulse uses the bearer token's implicit tenant,
 * but we still include `localStorage.pulse_token` so a logout fully invalidates. */
function tk() {
  return localStorage.getItem("pulse_token") || "anon";
}

// ---- Insights / Cherries -------------------------------------------------
export function useCherries(limit = 5) {
  return useQuery({
    queryKey: ["cherries", tk(), limit],
    queryFn: async () => (await getCherries(limit)).data,
  });
}

// ---- Home page bundle (11 endpoints) ------------------------------------
// HomeFeed is the most-visited page — every time the user returns, it used
// to refetch all 11 endpoints. Bundled in React Query: ~1.5s on cold load,
// ~0ms (cache) when returning within 30s. This is the biggest perceived
// win for daily implementer use.
import {
  getSummary,
  getTopInsights,
  getDemoHeadline,
  getMonthlyRevenue,
  getRevenueTimeseries,
  getMarkedActions,
  getPurchaseRecommendations,
  getZeroPriceInvoices,
  getOnTheWay,
  getFamilyProfitability,
} from "./api";

export function useHomeBundle() {
  return useQuery({
    queryKey: ["home-bundle", tk()],
    queryFn: async () => {
      const [s, ti, hl, mr, ts, mk, rec, ik, zp, otw, fam] = await Promise.all([
        getSummary(),
        getTopInsights(3),
        getDemoHeadline(),
        getMonthlyRevenue(),
        getRevenueTimeseries(60),
        getMarkedActions(),
        getPurchaseRecommendations(4),
        getInventoryKpis(),
        getZeroPriceInvoices(1),
        getOnTheWay(1),
        getFamilyProfitability(1),
      ]);
      return {
        summary: s.data,
        insights: ti.data.insights || [],
        headline: hl.data,
        monthly: mr.data.items || [],
        timeseries: ts.data,
        marked: mk.data.items || [],
        recommendations: rec.data.items || [],
        invKpis: ik.data,
        zeroPrice: zp.data,
        onTheWay: otw.data,
        families: fam.data,
      };
    },
  });
}

// ---- Inventory page: bundle of 5 endpoints --------------------------------
// The Inventory page needs 5 reads (KPIs + 4 tab lists). Bundling them into a
// single hook keeps the page code simple while still benefiting from cache:
// going home → /inventory → home → /inventory shows tiles instantly the
// second time around (was ~1.5s, now 0ms when within stale window).
import {
  getCriticalRestock,
  getStockouts,
  getDeadStock,
  getNegativeBalance,
} from "./api";

export function useInventoryBundle() {
  return useQuery({
    queryKey: ["inventory-bundle", tk()],
    queryFn: async () => {
      const [k, c, s, d, n] = await Promise.all([
        getInventoryKpis(),
        getCriticalRestock(20),
        getStockouts(20),
        getDeadStock(20, 60),
        getNegativeBalance(20),
      ]);
      return {
        kpis: k.data,
        critical: c.data.items || [],
        stockouts: s.data.items || [],
        dead: d.data.items || [],
        negative: n.data.items || [],
      };
    },
  });
}

// ---- BusinessInsights page: bundle of 13 endpoints ----------------------
// /insights is a long-scroll report page that fetched 13 endpoints serially-ish
// on every visit (~2-4s of blank "טוען תובנות..." each time). Bundling them
// into a single cached query makes the second visit feel instant.
import {
  getHeadline,
  getConcentration,
  getDayOfWeek,
  getBranches,
  getTopSellers,
  getFastGrowers,
  getPriceVariance,
  getTopInvoices,
  getLoyalInvoices,
} from "./api";

export function useBusinessInsightsBundle() {
  return useQuery({
    queryKey: ["business-insights-bundle", tk()],
    queryFn: async () => {
      const [h, ts, mr, c, dow, br, tsR, tsQ, fg, pv, recs, ti, li] = await Promise.all([
        getHeadline(),
        getRevenueTimeseries(120),
        getMonthlyRevenue(),
        getConcentration(),
        getDayOfWeek(),
        getBranches(),
        getTopSellers("revenue", 10),
        getTopSellers("qty", 10),
        getFastGrowers(),
        getPriceVariance(),
        getPurchaseRecommendations(8),
        getTopInvoices(5),
        getLoyalInvoices(5),
      ]);
      return {
        headline: h.data,
        timeseries: ts.data,
        monthly: mr.data.items,
        concentration: c.data,
        dow: dow.data.items,
        branches: br.data.items,
        topRevenue: tsR.data.items,
        topQty: tsQ.data.items,
        growers: fg.data.items,
        variance: pv.data.items,
        recommendations: recs.data.items,
        topInvoices: ti.data.items,
        loyalInvoices: li.data.items,
      };
    },
  });
}

// ---- Home / Briefs -------------------------------------------------------
export function useDailyBrief() {
  return useQuery({
    queryKey: ["daily-brief", tk()],
    queryFn: async () => (await getDailyBrief()).data,
  });
}

export function useExecutiveBrief() {
  return useQuery({
    queryKey: ["executive-brief", tk()],
    queryFn: async () => (await getExecutiveBrief()).data,
  });
}

// ---- Customer Churn Detector (tenant-specific) --------------------------
// Identifies at-risk customers based on historical buying patterns.
// Cache stays fresh for 30s — re-runs the pandas pipeline at most once
// every 30s of active use, instant otherwise.
import { getCustomerChurn } from "./api";

export function useCustomerChurn(limit = 200) {
  return useQuery({
    queryKey: ["customer-churn", tk(), limit],
    queryFn: async () => (await getCustomerChurn(limit)).data,
  });
}

// ---- AI Suggested Questions (tenant-specific quick prompts) -------------
// Built from the tenant's cherries — references real counts/₪. Same cache
// rules as inventory (30s stale). Frontend falls back to static prompts
// if this fails, so a network blip never leaves the rail empty.
export function useSuggestedQuestions() {
  return useQuery({
    queryKey: ["suggested-questions", tk()],
    queryFn: async () => (await getSuggestedQuestions()).data,
  });
}

// ---- AI Executive Actions ------------------------------------------------
// Involves an LLM call (Anthropic Claude). Keep it fresh longer (5 min stale)
// and DO NOT auto-refetch on focus — would burn LLM credits every tab focus.
export function useExecutiveActions(enabled = true) {
  return useQuery({
    queryKey: ["executive-actions", tk()],
    queryFn: async () => (await getExecutiveActions()).data,
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,  // LLM calls already retry server-side; don't double-charge
  });
}

